import wrapperHOC from "@Utils/wrapperHOC";
import { memo, useState, useMemo, useCallback, useEffect } from "react";
import { useSelector, useDispatch, shallowEqual } from "react-redux";
import type { AppState } from "@Store/index";
import styles from "./BlogGrid.module.scss";
import HeaderDesktop from "@Components/Header/HeaderDesktop";
import FooterDesktop from "@Components/FooterV2/FooterDesktop";
import Header from "@Components/Header";
import FooterV2 from "@Components/FooterV2/FooterV2";
import useIsDesktop from "hooks/useIsDesktop";
import NotFound from "@Pages/NotFound";
import ContentGridTop from "@Components/ContentGridTop";
import ContentCard from "@Components/ContentCard";
import Skeleton from "@Components/Skeleton";
import NoResultsCard from "@Components/NoResultsCard";
import CategoryTabs from "@Components/CategoryTabs";
import { useHistory } from "hooks/useHistory";
import { useLocation } from "react-router-dom";
import getBlogPageData from "../BlogPost/data/getBlogPageData";
import { updateBlogReadyStatus } from "../BlogPost/data/blogPageReducer";
import AppPromotionBanner from "@Components/AppPromotionBanner";
import { PAGE } from "constants/configConstants";
import { updateShowAppBanner } from "@App/appReducer";
import useBlogBasePath from "../utils/useBlogBasePath";
import { getBlogLanguageFromPath } from "@Pages/Blog/utils/blogLanguage";
import useTranslationHook from "hooks/useTranslationHook";

const BlogGrid = () => {
  const history = useHistory();
  const location = useLocation();
  const dispatch = useDispatch();
  const isDesktop = useIsDesktop();
  const basePath = useBlogBasePath();
  const lang = getBlogLanguageFromPath(basePath);
  const { t } = useTranslationHook();

  const {
    categoryData: blogCategoryData,
    readyStatus,
    totalDocs,
    isCategory,
    isTag,
    isSearch,
    isLatest,
    searchQuery,
    categories,
  } = useSelector((state: AppState) => state.blogPageData);

  const { showAppBanner } = useSelector(
    ({ appData }: AppState) => ({
      showAppBanner: appData.showAppBanner,
    }),
    shallowEqual,
  );

  const handleCloseBanner = () => {
    dispatch(updateShowAppBanner(false));
  };

  // Smooth scroll to top whenever the URL path changes (category/tag/search changes)
  useEffect(() => {
    window.scrollTo({
      top: 0,
      behavior: "smooth",
    });
  }, [location.pathname, searchQuery]);

  const [sortType, setSortType] = useState("newest");

  const isLoading = readyStatus === "request" || readyStatus === "invalid";
  const hasError = readyStatus === "failure";

  // Sort blog posts based on selected sort type
  const sortedBlogs = useMemo(() => {
    if (
      (!isCategory && !isTag && !isSearch && !isLatest) ||
      !blogCategoryData ||
      !Array.isArray(blogCategoryData)
    )
      return [];

    const blogs = [...blogCategoryData];
    if (sortType === "newest") {
      return blogs.sort(
        (a, b) =>
          new Date(b.updatedAt || b.date).getTime() - new Date(a.updatedAt || a.date).getTime(),
      );
    }
    if (sortType === "oldest") {
      return blogs.sort(
        (a, b) =>
          new Date(a.updatedAt || a.date).getTime() - new Date(b.updatedAt || b.date).getTime(),
      );
    }
    return blogs;
  }, [blogCategoryData, sortType, isCategory, isTag, isSearch, isLatest]);

  // Get name and slug based on whether it's a category or tag
  const itemName = useMemo(() => {
    if (isLatest) {
      return "Latest";
    }
    if (isCategory) {
      return blogCategoryData?.[0]?.category?.name || "";
    }
    if (isTag && blogCategoryData?.[0]?.matchedTag) {
      // Use the matched tag that was added on the server side
      return blogCategoryData[0].matchedTag.name || "";
    }
    return "";
  }, [blogCategoryData, isCategory, isTag, isSearch, isLatest]);

  const itemSlug = useMemo(() => {
    if (isLatest) {
      return "";
    }
    if (isCategory) {
      return blogCategoryData?.[0]?.category?.slug || "";
    }
    if (isTag && blogCategoryData?.[0]?.matchedTag) {
      return blogCategoryData[0].matchedTag.slug || "";
    }
    return "";
  }, [blogCategoryData, isCategory, isTag, isSearch, isLatest]);

  // eslint-disable-next-line no-nested-ternary
  const itemType = isLatest ? "latest" : isSearch ? "search" : isTag ? "tag" : "category";

  const handleSortChange = (newSortType: string) => {
    setSortType(newSortType);
  };

  const handleCategoryChange = useCallback(
    (categoryName: string) => {
      if (categoryName.toLowerCase() === "latest") {
        history.push("/blog/latest");
        return;
      }

      // Find the category object to get its slug
      const categoryObj = categories.find((cat) => cat.name === categoryName);
      if (categoryObj) {
        dispatch(updateBlogReadyStatus("request"));

        history.push(`/blog/category/${categoryObj.slug}`);

        // Fetch new category data
        dispatch(
          getBlogPageData({
            slug: categoryObj.slug,
            url: `/blog/category/${categoryObj.slug}`,
            isCategory: true,
            lang,
          }),
        );
      }
    },
    [categories, history, dispatch, lang],
  );

  const categoryTabsComponent = useMemo(() => {
    if ((isCategory || isLatest) && categories.length > 0) {
      return (
        <div className={styles.categoryTabsContainer}>
          <CategoryTabs
            categories={categories}
            activeCategory={itemName}
            onCategoryChange={handleCategoryChange}
            selectedSort={sortType}
            onSortChange={handleSortChange}
            showSortButton={totalDocs > 0}
            isGridPage
          />
        </div>
      );
    }
    return null;
  }, [
    categories,
    itemName,
    handleCategoryChange,
    isCategory,
    isLatest,
    sortType,
    handleSortChange,
    totalDocs,
  ]);

  if (isLoading) {
    return (
      <>
        {isDesktop ? (
          <HeaderDesktop isBlogHeader parentPage={PAGE.BLOG} />
        ) : (
          <>
            {showAppBanner && (
              <AppPromotionBanner from={PAGE.BLOG} onClose={handleCloseBanner} addSticky />
            )}
            <Header isBlogHeader />
          </>
        )}
        <div className={styles.blogCategoryContainer}>
          {categoryTabsComponent}
          <div className={styles.headerSection}>
            <Skeleton width="100%" height="20px" />
            <Skeleton width="300px" height="20px" />
          </div>
          <div className={styles.blogGrid}>
            {[1, 2, 3, 4, 5, 6, 7, 8].map((i) => (
              <div key={i} className={styles.skeletonCard}>
                <Skeleton width="100%" height="171px" />
                <div className={styles.skeletonContent}>
                  <Skeleton width="80px" height="18px" />
                  <Skeleton width="100%" height="48px" />
                </div>
              </div>
            ))}
          </div>
        </div>
        {isDesktop ? <FooterDesktop /> : <FooterV2 />}
      </>
    );
  }

  // Show 404 for invalid page types or actual errors (but not for empty search results)
  if (hasError || (!isCategory && !isTag && !isSearch && !isLatest)) {
    return <NotFound />;
  }

  // Show 404 for non-search pages with no results
  if (!isSearch && (!blogCategoryData || blogCategoryData.length === 0)) {
    return <NotFound />;
  }

  return (
    <>
      {isDesktop ? (
        <HeaderDesktop isBlogHeader parentPage={PAGE.BLOG} />
      ) : (
        <>
          {showAppBanner && (
            <AppPromotionBanner from={PAGE.BLOG} onClose={handleCloseBanner} addSticky />
          )}
          <Header isBlogHeader />
        </>
      )}
      <div className={styles.blogGridPageContainer}>
        {categoryTabsComponent}
        <ContentGridTop
          itemName={itemName}
          itemSlug={itemSlug}
          itemType={itemType}
          basePath={basePath}
          contentLabel={t("blog.grid.contentlabel")}
          breadcrumbLabel={t("blog.grid.breadcrumb.blogs")}
          searchQuery={searchQuery}
          totalItems={totalDocs}
          onSortChange={handleSortChange}
        />
        {sortedBlogs.length === 0 && isSearch ? (
          <NoResultsCard
            searchQuery={searchQuery}
            basePath={basePath}
            contentLabel={t("blog.grid.noresults.contentlabel")}
          />
        ) : (
          <div className={styles.blogGrid}>
            {sortedBlogs.map((blog) => (
              <ContentCard key={blog.id} item={blog} basePath={basePath} />
            ))}
          </div>
        )}
      </div>
      {isDesktop ? <FooterDesktop /> : <FooterV2 />}
    </>
  );
};

export default wrapperHOC(memo(BlogGrid), {
  componentName: "BlogGrid",
  showForChina: true,
});
